import { Core } from "@faire/design-tokens";
import {
  DEPRECATED_backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import { getFaireMarketUtils } from "@faire/web/common/promotional-events/getFaireMarketUtils";
import { Column } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { FaireMarketTheme } from "@faire/web/slate/Theme/EventsTheme";
import { eventsThemeColors } from "@faire/web/slate/Theme/eventsThemeColors";
import { EventsThemeShape } from "@faire/web/slate/Theme/EventsThemeShape";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ThemeProvider } from "@faire/web/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";
import styled from "styled-components";

import {
  getBestDeals,
  getIndependentBrands,
  insiderUpsell,
  saveTimeAndMoney,
} from "@faire/retailer/features/FaireMarkets/MarketHub/ValuePropsConsts";
import { AnimatedValueProps } from "@faire/retailer/features/FaireMarkets/ValueProps/AnimatedValueProps";
import {
  MarketStore,
  RetailerSegment,
} from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";

export const HomeFaireMarketValueProps: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { formatDateInMarketTimezone } = getFaireMarketUtils(
    undefined,
    strictLocalize
  );
  const valueProps = () => {
    const retailerSegment = MarketStore.get().retailerSegment;

    if (retailerSegment === RetailerSegment.INSIDER_ELIGIBLE) {
      return [getIndependentBrands(), getBestDeals(), insiderUpsell()];
    }
    return [getIndependentBrands(), getBestDeals(), saveTimeAndMoney()];
  };

  return (
    <ThemeProvider theme={FaireMarketTheme}>
      <Container>
        <ResponsiveContentV2
          tabletAndAbove={<StyledMarketBadge width="124px" />}
          mobile={<StyledMarketBadge width="92px" />}
        />

        <Content>
          <Header>
            <StrictLocalMsg
              defaultMessage="Now through {endDate}—shop the largest wholesale trade show from home."
              values={DEPRECATED_backwardsCompatibleValues({
                endDate: formatDateInMarketTimezone(
                  MarketStore.get().mainEventEndAt
                ),
              })}
            />
          </Header>
          <Spacer size="5x" />
          <StyledAnimatedValueProps
            valueProps={valueProps()}
            textColor={Core.text.default}
          />
        </Content>
      </Container>
    </ThemeProvider>
  );
};

const Container = styled.div`
  overflow: hidden;
  position: relative;
  max-width: 1280px;
  padding: ${getSpacing(112, "7x", 104)};
  margin: 0 auto;
  background-color: ${eventsThemeColors.faireMarket.lightBackground};

  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing("5x", "7x")};
  }

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("5x", "3x")};
  }
`;

const Content = styled(Column).attrs({
  align: {
    mobileAndAbove: "normal",
    tabletAndAbove: "center",
    desktopAndAbove: "normal",
  },
})``;

const Header = styled(Typography).attrs({
  variant: {
    desktopAndAbove: "displayMSerifRegular",
    tabletAndAbove: "displaySSerifRegular",
    mobileAndAbove: "pageHeaderSerifRegular",
  },
  color: Core.text.default,
})`
  &&& {
    ${responsiveMediaQueries.desktopAndAbove} {
      max-width: 663px;
    }

    ${responsiveMediaQueries.tablet} {
      z-index: 1;
    }

    ${responsiveMediaQueries.mobile} {
      margin-right: 60px;
    }
  }
`;

const StyledAnimatedValueProps = styled(AnimatedValueProps)`
  ${responsiveMediaQueries.tablet} {
    padding: unset;
  }
`;

const StyledMarketBadge = styled(EventsThemeShape.faireMarket.badge).attrs({
  color: eventsThemeColors.faireMarket.badgeIcon,
})`
  position: absolute;
  top: ${getSpacing("10x")};
  right: ${getSpacing("10x")};

  ${responsiveMediaQueries.tabletAndBelow} {
    top: ${getSpacing("4x")};
    right: -${getSpacing("4x")};
  }
`;
